import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../services/api";
import ProtectedLink from "../../components/ProtectedLink";

// ── Constants ──────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending:        { label: "Reçue",           color: "#185fa5", bg: "#185fa515", icon: "📥" },
  validated:      { label: "Validée",          color: "#7c3aed", bg: "#7c3aed15", icon: "✅" },
  in_preparation: { label: "En préparation",   color: "#d97706", bg: "#d9770615", icon: "🔵" },
  ready:          { label: "Prête",            color: "#16a34a", bg: "#16a34a15", icon: "🟢" },
  shipped:        { label: "Expédiée",         color: "#6b7280", bg: "#6b728015", icon: "🚚" },
  delivered:      { label: "Livrée",           color: "#1a9e72", bg: "#1a9e7215", icon: "📦" },
  cancelled:      { label: "Annulée",          color: "#e24b4a", bg: "#e24b4a15", icon: "✕" },
};
const SOURCE_CONFIG = {
  manual:   { label: "Manuel",      icon: "✏️", color: "#6b7280" },
  excel:    { label: "Excel",       icon: "📊", color: "#16a34a" },
  pwa:      { label: "PWA",         icon: "📱", color: "#7c3aed" },
  ocr_pdf:  { label: "OCR PDF",     icon: "🤖", color: "#185fa5" },
  api:      { label: "API",         icon: "🔗", color: "#d97706" },
};
const STATUS_FLOW = {
  pending:        ["validated", "cancelled"],
  validated:      ["in_preparation", "cancelled"],
  in_preparation: ["ready", "cancelled"],
  ready:          ["shipped"],
  shipped:        ["delivered"],
};

function fmt(n) { return (n||0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { if (!d) return "—"; return new Date(d + "T00:00:00").toLocaleDateString("fr-FR"); }

// ── Plan guard banner ─────────────────────────────────────────────────────
function PlanBanner({ feature, minPlan, onNavigate }) {
  return (
    <div style={{ padding: "20px 24px", background: "linear-gradient(135deg,#7c3aed15,#185fa515)", border: "1px solid #7c3aed33", borderRadius: 12, textAlign: "center" }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{feature} — Plan {minPlan} requis</div>
      <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 14 }}>Cette fonctionnalité est disponible à partir du plan {minPlan}.</div>
      <button className="btn primary" onClick={() => onNavigate?.('monabonnement')}>⬆️ Passer au plan {minPlan}</button>
    </div>
  );
}

// ── Create Order Form ─────────────────────────────────────────────────────
function CreateOrderModal({ products, onClose, onCreated, currentCompanyId }) {
  const [form, setForm] = useState({ client_name: "", client_email: "", delivery_address: "", order_date: new Date().toISOString().slice(0,10), expected_delivery: "", tva_rate: "20", notes: "" });
  const [lines, setLines] = useState([{ product_id: "", product_ref: "", product_name: "", quantity_ordered: "1", unit_price: "" }]);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const addLine    = () => setLines(l => [...l, { product_id: "", product_ref: "", product_name: "", quantity_ordered: "1", unit_price: "" }]);
  const removeLine = (i) => setLines(l => l.filter((_, idx) => idx !== i));
  const setLine    = (i, f, v) => setLines(l => l.map((ln, idx) => idx === i ? { ...ln, [f]: v } : ln));

  const selectProduct = (i, val) => {
    const p = products.find(p => p.name === val || p.sku === val);
    if (p) setLines(l => l.map((ln, idx) => idx === i ? { ...ln, product_id: String(p.id), product_ref: p.sku||"", product_name: p.name, unit_price: String(p.selling_price||p.price_sale||0) } : ln));
    else   setLine(i, "product_name", val);
  };

  const totalHt = lines.reduce((s, l) => s + (parseFloat(l.unit_price||0) * parseInt(l.quantity_ordered||1)), 0);
  const totalTtc = totalHt * (1 + parseFloat(form.tva_rate||20)/100);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const r = await api.customerOrders.create({ ...form, company_id: currentCompanyId, tva_rate: parseFloat(form.tva_rate), lines: lines.map(l => ({ ...l, product_id: l.product_id || null, quantity_ordered: parseInt(l.quantity_ordered||1), unit_price: parseFloat(l.unit_price||0) })) });
      onCreated(r.order_number); onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div className="card" style={{ width: "100%", maxWidth: 680 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>✏️ Nouvelle commande client</span>
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button>
        </div>
        {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Client *</label>
              <input className="input" required value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} placeholder="Nom du client ou société" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Email client</label>
              <input className="input" type="email" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>TVA</label>
              <select className="input" value={form.tva_rate} onChange={e => setForm(f => ({ ...f, tva_rate: e.target.value }))}>
                <option value="20">20%</option><option value="10">10%</option><option value="5.5">5,5%</option><option value="0">0%</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date commande *</label>
              <input className="input" type="date" required value={form.order_date} onChange={e => setForm(f => ({ ...f, order_date: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Livraison prévue</label>
              <input className="input" type="date" value={form.expected_delivery} onChange={e => setForm(f => ({ ...f, expected_delivery: e.target.value }))} />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Adresse de livraison</label>
              <input className="input" value={form.delivery_address} onChange={e => setForm(f => ({ ...f, delivery_address: e.target.value }))} placeholder="1 rue de la Paix, 75001 Paris" />
            </div>
          </div>

          {/* Lignes produits */}
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, borderTop: "1px solid #f3f4f6", paddingTop: 12 }}>Lignes produits *</div>
          {lines.map((l, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 8, marginBottom: 8, alignItems: "end" }}>
              <div>
                {i === 0 && <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 3, color: "var(--text-muted)" }}>Produit</label>}
                <input className="input" list={`products-list-${i}`} value={l.product_name} onChange={e => selectProduct(i, e.target.value)} placeholder="Rechercher produit…" required />
                <datalist id={`products-list-${i}`}>{products.map(p => <option key={p.id} value={p.name}>{p.sku ? `[${p.sku}] ` : ''}{p.name}</option>)}</datalist>
              </div>
              <div>
                {i === 0 && <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 3, color: "var(--text-muted)" }}>Quantité</label>}
                <input className="input" type="number" min="1" value={l.quantity_ordered} onChange={e => setLine(i, "quantity_ordered", e.target.value)} required />
              </div>
              <div>
                {i === 0 && <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 3, color: "var(--text-muted)" }}>P.U. HT (€)</label>}
                <input className="input" type="number" step="0.01" value={l.unit_price} onChange={e => setLine(i, "unit_price", e.target.value)} placeholder="0.00" />
              </div>
              <button type="button" className="btn" style={{ color: "#e24b4a", padding: "8px 10px" }} onClick={() => removeLine(i)} disabled={lines.length === 1}>✕</button>
            </div>
          ))}
          <button type="button" className="btn" style={{ fontSize: 12, marginBottom: 16 }} onClick={addLine}>+ Ajouter une ligne</button>

          {totalHt > 0 && (
            <div style={{ display: "flex", gap: 12, marginBottom: 16, padding: "10px 14px", background: "#f9fafb", borderRadius: 8 }}>
              {[["Total HT", fmt(totalHt) + " €"], ["TVA " + form.tva_rate + "%", fmt(totalTtc - totalHt) + " €"], ["Total TTC", fmt(totalTtc) + " €"]].map(([l, v]) => (
                <div key={l} style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{l}</div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{v}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Notes</label>
            <textarea className="input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Instructions de livraison, priorité…" style={{ resize: "vertical" }} />
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? "Enregistrement…" : "Créer la commande"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Excel Import Modal ────────────────────────────────────────────────────
function ExcelImportModal({ onClose, onImported, currentCompanyId }) {
  const [file, setFile]         = useState(null);
  const [preview, setPreview]   = useState(null);
  const [errors, setErrors]     = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f); setPreview(null); setErrors([]); setWarnings([]); setLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const b64 = btoa(String.fromCharCode(...new Uint8Array(e.target.result)));
      try {
        const r = await api.customerOrders.importExcel({ company_id: currentCompanyId, file_base64: b64, dry_run: true });
        setPreview(r.preview); setErrors(r.errors||[]); setWarnings(r.warnings||[]);
      } catch (err) { setErrors([{ row: 0, message: err.message }]); }
      finally { setLoading(false); }
    };
    reader.readAsArrayBuffer(f);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const b64 = btoa(String.fromCharCode(...new Uint8Array(e.target.result)));
      try {
        const r = await api.customerOrders.importExcel({ company_id: currentCompanyId, file_base64: b64, dry_run: false });
        onImported(r.imported); onClose();
      } catch (err) { setErrors([{ row: 0, message: err.message }]); }
      finally { setImporting(false); }
    };
    reader.readAsArrayBuffer(file);
  };

  const blockingErrors = errors.filter(e => e.row > 0);

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div className="card" style={{ width: "100%", maxWidth: 720 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>📊 Import commandes Excel</span>
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button>
        </div>

        <div style={{ marginBottom: 16, padding: "10px 14px", background: "#f9fafb", borderRadius: 8, fontSize: 13 }}>
          Colonnes attendues : <code style={{ background: "#e5e7eb", padding: "1px 4px", borderRadius: 4 }}>order_number | client_name | product_ref | product_name | quantity | unit_price | expected_delivery | notes</code>
          <ProtectedLink url={api.customerOrders.templateUrl()} style={{ marginLeft: 10, color: "var(--g)", fontWeight: 600, textDecoration: "none" }}>⬇️ Template Excel</ProtectedLink>
        </div>

        {/* Drop zone */}
        <div
          style={{ border: "2px dashed #d1d5db", borderRadius: 10, padding: "32px 24px", textAlign: "center", cursor: "pointer", background: file ? "#f0fdf4" : "#fafafa", marginBottom: 16 }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => fileRef.current?.click()}
        >
          <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{file ? file.name : "Glissez un fichier .xlsx/.csv ici"}</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{file ? `${(file.size/1024).toFixed(1)} KB` : "ou cliquez pour sélectionner"}</div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
        </div>

        {loading && <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>Analyse en cours…</div>}

        {/* Errors */}
        {errors.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            {errors.map((e, i) => (
              <div key={i} style={{ padding: "6px 10px", background: "#e24b4a15", border: "1px solid #e24b4a30", borderRadius: 6, fontSize: 12, color: "#e24b4a", marginBottom: 4 }}>
                {e.row > 0 ? `Ligne ${e.row} : ` : ""}{e.message}
              </div>
            ))}
          </div>
        )}
        {warnings.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            {warnings.map((w, i) => (
              <div key={i} style={{ padding: "6px 10px", background: "#f59e0b15", border: "1px solid #f59e0b30", borderRadius: 6, fontSize: 12, color: "#d97706", marginBottom: 4 }}>
                ⚠️ Ligne {w.row} : {w.message}
              </div>
            ))}
          </div>
        )}

        {/* Preview */}
        {preview && preview.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
              Aperçu — {preview.length} commande(s) · {preview.reduce((s, o) => s + o.lines.length, 0)} lignes
              {warnings.length > 0 && <span style={{ marginLeft: 8, fontSize: 12, color: "#d97706" }}>⚠️ {warnings.length} avertissement(s)</span>}
            </div>
            <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #f3f4f6", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead style={{ position: "sticky", top: 0 }}>
                  <tr style={{ background: "#f8fafc", color: "var(--text-muted)" }}>
                    <th style={{ padding: "7px 10px", textAlign: "left" }}>N° Commande</th>
                    <th style={{ padding: "7px 10px", textAlign: "left" }}>Client</th>
                    <th style={{ padding: "7px 10px", textAlign: "right" }}>Lignes</th>
                    <th style={{ padding: "7px 10px", textAlign: "right" }}>Total HT</th>
                    <th style={{ padding: "7px 10px", textAlign: "left" }}>Livraison</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((o, i) => {
                    const ht = o.lines.reduce((s, l) => s + (l.unit_price||0) * (l.quantity_ordered||1), 0);
                    return (
                      <tr key={i} style={{ borderTop: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "6px 10px", fontFamily: "monospace", fontWeight: 600 }}>{o.order_number}</td>
                        <td style={{ padding: "6px 10px" }}>{o.client_name}</td>
                        <td style={{ padding: "6px 10px", textAlign: "right" }}>{o.lines.length}</td>
                        <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600 }}>{ht.toFixed(2)} €</td>
                        <td style={{ padding: "6px 10px", color: "var(--text-muted)" }}>{o.expected_delivery || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn" onClick={onClose}>Annuler</button>
          <button className="btn primary" disabled={!preview || blockingErrors.length > 0 || importing} onClick={handleImport}>
            {importing ? "Importation…" : `Importer ${preview?.length || 0} commande(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PDF OCR Modal ─────────────────────────────────────────────────────────
function OcrPdfModal({ products, onClose, onCreated, currentCompanyId }) {
  const [file, setFile]         = useState(null);
  const [scanning, setScanning] = useState(false);
  const [ocrData, setOcrData]   = useState(null);
  const [form, setForm]         = useState(null);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const fileRef = useRef();

  const scanPdf = async (f) => {
    setFile(f); setOcrData(null); setForm(null); setError(""); setScanning(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const arr = new Uint8Array(e.target.result);
      let b64 = '';
      for (let i = 0; i < arr.length; i += 32768) {
        b64 += String.fromCharCode(...arr.subarray(i, i + 32768));
      }
      b64 = btoa(b64);
      try {
        const r = await api.customerOrders.ocrPdf({ pdf_base64: b64 });
        const d = r.data;
        setOcrData(d);
        setForm({
          client_name: d.client_name || "",
          client_email: d.client_email || "",
          delivery_address: d.delivery_address || "",
          order_date: d.order_date || new Date().toISOString().slice(0, 10),
          expected_delivery: d.expected_delivery || "",
          notes: "",
          lines: (d.lines || []).map(l => ({
            product_id: String(l.product_id||""),
            product_ref: l.product_ref||"",
            product_name: l.product_name||"",
            quantity_ordered: String(l.quantity||1),
            unit_price: String(l.unit_price||0),
          })),
        });
      } catch (err) {
        if (err.message.includes('fallback')) {
          setError("Lecture automatique indisponible — passez en saisie manuelle.");
        } else {
          setError(err.message);
        }
      }
      finally { setScanning(false); }
    };
    reader.readAsArrayBuffer(f);
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true); setError("");
    try {
      const r = await api.customerOrders.create({ ...form, company_id: currentCompanyId, tva_rate: 20, source: 'ocr_pdf', lines: form.lines.map(l => ({ ...l, product_id: l.product_id||null, quantity_ordered: parseInt(l.quantity_ordered||1), unit_price: parseFloat(l.unit_price||0) })) });
      onCreated(r.order_number); onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const setFormLine = (i, f, v) => setForm(prev => ({ ...prev, lines: prev.lines.map((l, idx) => idx === i ? { ...l, [f]: v } : l) }));
  const addLine = () => setForm(prev => ({ ...prev, lines: [...prev.lines, { product_id: "", product_ref: "", product_name: "", quantity_ordered: "1", unit_price: "0" }] }));
  const removeLine = (i) => setForm(prev => ({ ...prev, lines: prev.lines.filter((_, idx) => idx !== i) }));

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div className="card" style={{ width: "100%", maxWidth: 700 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>🤖 Import commande par OCR PDF</span>
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button>
        </div>

        {!form ? (
          <>
            <div style={{ marginBottom: 12, padding: "10px 14px", background: "#185fa508", border: "1px solid #185fa520", borderRadius: 8, fontSize: 13, color: "#185fa5" }}>
              Importez un bon de commande PDF. L'IA extrait automatiquement les données pour pré-remplir le formulaire.
            </div>
            {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <div
              style={{ border: "2px dashed #d1d5db", borderRadius: 10, padding: "40px 24px", textAlign: "center", cursor: "pointer", background: "#fafafa", marginBottom: 16 }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); scanPdf(e.dataTransfer.files[0]); }}
              onClick={() => !scanning && fileRef.current?.click()}
            >
              {scanning ? (
                <>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🤖</div>
                  <div style={{ fontWeight: 600 }}>Analyse en cours par l'IA…</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>Extraction des données du bon de commande</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{file ? file.name : "Glissez un PDF ici"}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>ou cliquez pour sélectionner · PDF uniquement</div>
                </>
              )}
              <input ref={fileRef} type="file" accept=".pdf" style={{ display: "none" }} onChange={e => scanPdf(e.target.files[0])} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn" onClick={onClose}>Fermer</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: "8px 12px", background: "#1a9e7215", border: "1px solid #1a9e7230", borderRadius: 8, marginBottom: 16, fontSize: 13, color: "#1a9e72" }}>
              ✅ Données extraites par l'IA — vérifiez et corrigez avant de valider.
            </div>
            {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Client</label>
                <input className="input" value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date commande</label>
                <input className="input" type="date" value={form.order_date} onChange={e => setForm(f => ({ ...f, order_date: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Livraison prévue</label>
                <input className="input" type="date" value={form.expected_delivery} onChange={e => setForm(f => ({ ...f, expected_delivery: e.target.value }))} />
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Adresse livraison</label>
                <input className="input" value={form.delivery_address} onChange={e => setForm(f => ({ ...f, delivery_address: e.target.value }))} />
              </div>
            </div>

            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Lignes produits ({form.lines.length})</div>
            {form.lines.map((l, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 8, marginBottom: 8, alignItems: "end" }}>
                <div>
                  {i === 0 && <label style={{ fontSize: 11, color: "var(--text-muted)", display: "block", marginBottom: 3 }}>Produit</label>}
                  <input className="input" list={`ocr-prod-${i}`} value={l.product_name} onChange={e => setFormLine(i, "product_name", e.target.value)} />
                  <datalist id={`ocr-prod-${i}`}>{products.map(p => <option key={p.id} value={p.name} />)}</datalist>
                </div>
                <div>
                  {i === 0 && <label style={{ fontSize: 11, color: "var(--text-muted)", display: "block", marginBottom: 3 }}>Qté</label>}
                  <input className="input" type="number" min="1" value={l.quantity_ordered} onChange={e => setFormLine(i, "quantity_ordered", e.target.value)} />
                </div>
                <div>
                  {i === 0 && <label style={{ fontSize: 11, color: "var(--text-muted)", display: "block", marginBottom: 3 }}>P.U. HT €</label>}
                  <input className="input" type="number" step="0.01" value={l.unit_price} onChange={e => setFormLine(i, "unit_price", e.target.value)} />
                </div>
                <button type="button" className="btn" style={{ color: "#e24b4a" }} onClick={() => removeLine(i)} disabled={form.lines.length === 1}>✕</button>
              </div>
            ))}
            <button className="btn" style={{ fontSize: 12, marginBottom: 16 }} onClick={addLine}>+ Ligne</button>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setForm(null)}>← Rescanner</button>
              <button className="btn" onClick={onClose}>Annuler</button>
              <button className="btn primary" disabled={saving} onClick={handleSave}>{saving ? "Importation…" : "Valider et importer"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── API Keys Modal ────────────────────────────────────────────────────────
function ApiKeysModal({ currentCompanyId, onClose }) {
  const [keys, setKeys]       = useState([]);
  const [keyName, setKeyName] = useState("");
  const [saving, setSaving]   = useState(false);

  const load = () => api.customerOrders.apiKeys(currentCompanyId).then(setKeys).catch(console.error);
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setSaving(true);
    try { await api.customerOrders.createApiKey({ company_id: currentCompanyId, key_name: keyName || 'Clé API' }); setKeyName(""); load(); }
    finally { setSaving(false); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette clé API ?")) return;
    await api.customerOrders.deleteApiKey(id); load();
  };
  const handleToggle = async (id) => { await api.customerOrders.toggleApiKey(id); load(); };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="card" style={{ width: "100%", maxWidth: 580 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>🔗 Clés API externes</span>
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button>
        </div>
        <div style={{ marginBottom: 14, padding: "10px 14px", background: "#f9fafb", borderRadius: 8, fontSize: 12 }}>
          Endpoint webhook : <code style={{ background: "#e5e7eb", padding: "2px 6px", borderRadius: 4 }}>POST /api/customer-orders/webhook</code>
          <br />Header requis : <code style={{ background: "#e5e7eb", padding: "2px 6px", borderRadius: 4, marginTop: 4, display: "inline-block" }}>X-API-Key: votre_clé</code>
        </div>
        {keys.length === 0 ? (
          <div style={{ textAlign: "center", padding: 24, color: "var(--text-muted)", fontSize: 13 }}>Aucune clé API. Générez-en une ci-dessous.</div>
        ) : keys.map(k => (
          <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{k.key_name}</div>
              <div style={{ fontFamily: "monospace", fontSize: 11, color: "var(--text-muted)", marginTop: 2, wordBreak: "break-all" }}>{k.key_value}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                {k.call_count} appel(s) · Dernière utilisation : {k.last_used ? new Date(k.last_used).toLocaleDateString("fr-FR") : "Jamais"}
              </div>
            </div>
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, fontWeight: 700, background: k.is_active ? "#1a9e7215" : "#6b728015", color: k.is_active ? "#1a9e72" : "#6b7280" }}>{k.is_active ? "Active" : "Inactive"}</span>
            <button className="btn" style={{ fontSize: 11 }} onClick={() => handleToggle(k.id)}>{k.is_active ? "Désactiver" : "Activer"}</button>
            <button className="btn" style={{ fontSize: 11, color: "#e24b4a" }} onClick={() => handleDelete(k.id)}>🗑</button>
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <input className="input" value={keyName} onChange={e => setKeyName(e.target.value)} placeholder="Nom de la clé (ex: ERP Sage)" style={{ flex: 1 }} />
          <button className="btn primary" disabled={saving} onClick={handleCreate}>{saving ? "…" : "Générer une clé"}</button>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8 }}>⚠️ La clé complète n'est visible qu'une seule fois après génération. Copiez-la immédiatement.</div>
      </div>
    </div>
  );
}

// ── Order Detail Modal ────────────────────────────────────────────────────
function OrderDetailModal({ order, onClose, onStatusChange, onCreatePrep }) {
  const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const nextStatuses = STATUS_FLOW[order.status] || [];

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div className="card" style={{ width: "100%", maxWidth: 600 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, fontFamily: "monospace" }}>{order.order_number}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{order.client_name || "—"}</div>
          </div>
          <span style={{ marginLeft: "auto", fontSize: 11, padding: "4px 10px", borderRadius: 10, fontWeight: 700, background: sc.bg, color: sc.color }}>{sc.icon} {sc.label}</span>
          <button className="btn" style={{ marginLeft: 10 }} onClick={onClose}>✕</button>
        </div>

        {/* Infos */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
          {[["Date", fmtDate(order.order_date)], ["Livraison prévue", fmtDate(order.expected_delivery)], ["Canal", `${SOURCE_CONFIG[order.source]?.icon||""} ${SOURCE_CONFIG[order.source]?.label||order.source}`]].map(([l, v]) => (
            <div key={l} style={{ background: "#f9fafb", borderRadius: 8, padding: "8px 12px" }}>
              <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{l}</div>
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Lines */}
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Lignes produits ({order.lines?.length || 0})</div>
        <div className="card" style={{ padding: 0, marginBottom: 16, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f8fafc", color: "var(--text-muted)", fontSize: 11 }}>
                <th style={{ padding: "7px 10px", textAlign: "left" }}>Réf.</th>
                <th style={{ padding: "7px 10px", textAlign: "left" }}>Désignation</th>
                <th style={{ padding: "7px 10px", textAlign: "right" }}>Qté</th>
                <th style={{ padding: "7px 10px", textAlign: "right" }}>P.U. HT</th>
                <th style={{ padding: "7px 10px", textAlign: "right" }}>Total HT</th>
              </tr>
            </thead>
            <tbody>
              {(order.lines||[]).map(l => (
                <tr key={l.id} style={{ borderTop: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "6px 10px", fontFamily: "monospace", fontSize: 11 }}>{l.product_ref||"—"}</td>
                  <td style={{ padding: "6px 10px" }}>{l.product_name}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, fontSize: 14 }}>{l.quantity_ordered}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right" }}>{fmt(l.unit_price)} €</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }}>{fmt(l.total_ht)} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 4, marginBottom: 12, padding: "8px 0", borderTop: "1px solid #f3f4f6" }}>
          <span style={{ marginRight: "auto", fontSize: 13 }}>
            <strong>Total HT :</strong> {fmt(order.total_ht)} € &nbsp;|&nbsp; <strong>Total TTC :</strong> {fmt(order.total_ttc)} €
          </span>
        </div>

        {order.delivery_address && (
          <div style={{ marginBottom: 16, padding: "8px 12px", background: "#f9fafb", borderRadius: 8, fontSize: 12 }}>
            <strong>Adresse livraison :</strong> {order.delivery_address}
          </div>
        )}
        {order.notes && (
          <div style={{ marginBottom: 16, padding: "8px 12px", background: "#f9fafb", borderRadius: 8, fontSize: 12 }}>
            <strong>Notes :</strong> {order.notes}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {nextStatuses.map(s => {
            const ns = STATUS_CONFIG[s];
            return (
              <button key={s} className="btn" style={{ background: ns.bg, color: ns.color, border: `1px solid ${ns.color}33`, fontWeight: 600 }} onClick={() => { onStatusChange(order.id, s); onClose(); }}>
                {ns.icon} → {ns.label}
              </button>
            );
          })}
          {order.status === 'validated' && (
            <button className="btn primary" onClick={() => { onCreatePrep(order.id); onClose(); }}>🚚 Lancer préparation</button>
          )}
          <ProtectedLink newTab url={api.customerOrders.shippingLabel(order.id)}>
            <button className="btn" style={{ fontSize: 12 }}>📄 Bordereau PDF</button>
          </ProtectedLink>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Main CustomerOrdersTab
// ════════════════════════════════════════════════════════════════════════
const SAAS_MODE = process.env.REACT_APP_SAAS_MODE === 'true';

export default function CustomerOrdersTab({ currentCompanyId, products = [], onRefresh, onNavigate }) {
  const [orders, setOrders]           = useState([]);
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch]           = useState("");
  const [showCreate, setShowCreate]   = useState(false);
  const [showExcel, setShowExcel]     = useState(false);
  const [showOcr, setShowOcr]         = useState(false);
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [toast, setToast]             = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    Promise.all([
      api.customerOrders.list(currentCompanyId, { status: statusFilter||undefined, search: search||undefined }),
      api.customerOrders.stats(currentCompanyId),
    ]).then(([r, s]) => {
      setOrders(r.orders || []);
      setStats(s);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id, status) => {
    await api.customerOrders.status(id, status).catch(console.error);
    load(); onRefresh?.();
  };

  const handleCreatePrep = async (id) => {
    try {
      await api.customerOrders.createPrep(id, {});
      showToast("✅ Préparation lancée — visible dans l'onglet Préparation.");
      load(); onRefresh?.();
    } catch (err) { alert(err.message); }
  };

  const openDetail = async (order) => {
    try {
      const full = await api.customerOrders.get(order.id);
      setDetailOrder(full);
    } catch { setDetailOrder(order); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette commande ?")) return;
    await api.customerOrders.delete(id).catch(console.error); load();
  };

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 2000, background: "#1a9e72", color: "#fff", padding: "12px 20px", borderRadius: 10, fontWeight: 600, fontSize: 14, boxShadow: "0 4px 20px #0003" }}>
          {toast}
        </div>
      )}

      {/* KPIs */}
      {stats && (
        <div className="kpi-grid k4" style={{ marginBottom: 16 }}>
          <div className="kpi"><div className="kpi-label">📥 Reçues</div><div className="kpi-value">{stats.pending}</div><div className="kpi-sub">À valider</div></div>
          <div className="kpi"><div className="kpi-label">🔵 En préparation</div><div className="kpi-value" style={{ color: "#d97706" }}>{stats.in_preparation}</div></div>
          <div className="kpi"><div className="kpi-label">🚚 Expédiées auj.</div><div className="kpi-value" style={{ color: "#1a9e72" }}>{stats.shipped_today}</div></div>
          <div className="kpi"><div className="kpi-label">💶 CA total HT</div><div className="kpi-value">{(stats.total_ht||0).toLocaleString("fr-FR",{maximumFractionDigits:0})} €</div></div>
        </div>
      )}

      {/* Header actions */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <button className="btn primary" onClick={() => setShowCreate(true)}>+ Nouvelle commande</button>
        <button className="btn" onClick={() => setShowExcel(true)}>📊 Import Excel</button>
        {SAAS_MODE ? (
          <div title="Plan PME requis">
            <button className="btn" disabled style={{ opacity: 0.5 }}>🤖 OCR PDF 🔒</button>
          </div>
        ) : (
          <button className="btn" onClick={() => setShowOcr(true)}>🤖 OCR PDF</button>
        )}
        {SAAS_MODE ? (
          <div title="Plan Groupe requis">
            <button className="btn" disabled style={{ opacity: 0.5 }}>🔗 API externe 🔒</button>
          </div>
        ) : (
          <button className="btn" onClick={() => setShowApiKeys(true)}>🔗 API externe</button>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <input className="input" placeholder="Rechercher…" value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 200 }} />
          <ProtectedLink newTab url={api.customerOrders.exportUrl(currentCompanyId)}>
            <button className="btn">⬇️ Export Excel</button>
          </ProtectedLink>
        </div>
      </div>

      {/* Canal banners in SAAS mode */}
      {SAAS_MODE && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ padding: "10px 14px", background: "#7c3aed08", border: "1px solid #7c3aed20", borderRadius: 8, fontSize: 12 }}>
            🤖 <strong>OCR PDF</strong> — Importez des bons de commande PDF (plan PME)
            <button className="btn" style={{ fontSize: 11, marginLeft: 8, color: "#7c3aed" }} onClick={() => onNavigate?.('monabonnement')}>Passer au plan PME</button>
          </div>
          <div style={{ padding: "10px 14px", background: "#d9770608", border: "1px solid #d9770620", borderRadius: 8, fontSize: 12 }}>
            🔗 <strong>API externe</strong> — Connectez votre ERP ou marketplace (plan Groupe)
            <button className="btn" style={{ fontSize: 11, marginLeft: 8, color: "#d97706" }} onClick={() => onNavigate?.('monabonnement')}>Passer au plan Groupe</button>
          </div>
        </div>
      )}

      {/* Status filter */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        <button onClick={() => setStatusFilter("")} style={{ padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "1px solid #e5e7eb", background: !statusFilter ? "var(--g)" : "#fff", color: !statusFilter ? "#fff" : "var(--text-muted)" }}>
          Toutes ({stats?.total||0})
        </button>
        {Object.entries(STATUS_CONFIG).map(([k, sc]) => (
          <button key={k} onClick={() => setStatusFilter(k === statusFilter ? "" : k)} style={{ padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${sc.color}33`, background: statusFilter === k ? sc.bg : "#fff", color: statusFilter === k ? sc.color : "var(--text-muted)" }}>
            {sc.icon} {sc.label} {stats?.[k] != null ? `(${stats[k]})` : ""}
          </button>
        ))}
      </div>

      {/* Orders list */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "var(--text-muted)" }}>Chargement…</div>
      ) : orders.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 56 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📥</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Aucune commande client{statusFilter ? ` (${STATUS_CONFIG[statusFilter]?.label})` : ""}</div>
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 20 }}>Saisissez manuellement, importez un Excel ou déposez un PDF de bon de commande.</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button className="btn primary" onClick={() => setShowCreate(true)}>+ Saisie manuelle</button>
            <button className="btn" onClick={() => setShowExcel(true)}>📊 Import Excel</button>
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
            <thead>
              <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>N° Commande</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Client</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Date</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Livraison</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Canal</th>
                <th style={{ padding: "9px 14px", textAlign: "right", fontWeight: 600 }}>Total HT</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Statut</th>
                <th style={{ padding: "9px 14px", fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => {
                const sc  = STATUS_CONFIG[o.status] || STATUS_CONFIG.pending;
                const src = SOURCE_CONFIG[o.source] || SOURCE_CONFIG.manual;
                return (
                  <tr key={o.id} style={{ borderTop: "1px solid #f3f4f6", fontSize: 13, cursor: "pointer" }} onClick={() => openDetail(o)}>
                    <td style={{ padding: "10px 14px", fontFamily: "monospace", fontWeight: 700, color: "var(--g)" }}>{o.order_number}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 600 }}>{o.client_name || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "var(--text-muted)" }}>{fmtDate(o.order_date)}</td>
                    <td style={{ padding: "10px 14px", color: "var(--text-muted)" }}>{fmtDate(o.expected_delivery)}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ fontSize: 11, color: src.color }}>{src.icon} {src.label}</span>
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700 }}>{fmt(o.total_ht)} €</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 10, fontWeight: 700, background: sc.bg, color: sc.color }}>{sc.icon} {sc.label}</span>
                    </td>
                    <td style={{ padding: "10px 14px" }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: "flex", gap: 4 }}>
                        {o.status === 'validated' && (
                          <button className="btn" style={{ fontSize: 10, color: "#d97706" }} onClick={() => handleCreatePrep(o.id)}>🚚 Préparer</button>
                        )}
                        {['pending','cancelled'].includes(o.status) && (
                          <button className="btn" style={{ fontSize: 10, color: "#e24b4a" }} onClick={() => handleDelete(o.id)}>🗑</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showCreate && <CreateOrderModal products={products} currentCompanyId={currentCompanyId} onClose={() => setShowCreate(false)} onCreated={(n) => { showToast(`✅ Commande ${n} créée`); load(); onRefresh?.(); }} />}
      {showExcel  && <ExcelImportModal currentCompanyId={currentCompanyId} onClose={() => setShowExcel(false)} onImported={(n) => { showToast(`✅ ${n} commande(s) importée(s)`); load(); onRefresh?.(); }} />}
      {showOcr    && <OcrPdfModal products={products} currentCompanyId={currentCompanyId} onClose={() => setShowOcr(false)} onCreated={(n) => { showToast(`✅ Commande ${n} créée via OCR`); load(); onRefresh?.(); }} />}
      {showApiKeys && <ApiKeysModal currentCompanyId={currentCompanyId} onClose={() => setShowApiKeys(false)} />}
      {detailOrder && <OrderDetailModal order={detailOrder} onClose={() => setDetailOrder(null)} onStatusChange={handleStatusChange} onCreatePrep={handleCreatePrep} />}
    </div>
  );
}
